/**
 * Sort two ULIDs lexicographically
 * @param {string} ulid1 - The first ULID
 * @param {string} ulid2 - The second ULID
 * @returns {number} -1 if ulid1 is before ulid2, 1 if ulid1 is after ulid2, or 0 if they are (exactly) equal or either are missing
 */
export const sortULIDs = (ulid1, ulid2) => {
  // Use == null (not falsy check) to allow empty string as valid LOWEST_KEY sentinel
  if (ulid1 == null || ulid2 == null) return 0;
  return ulid1.localeCompare(ulid2);
};

/**
 * Escapes special regex characters to prevent regex syntax errors
 * Uses native RegExp.escape when available, with fallback for older browsers
 * @param {string} string - The string to escape
 * @returns {string} The escaped string safe for use in regex
 */
export const escapeRegex = (string) => {
  // Use native RegExp.escape if available (ES2026)
  if (typeof RegExp.escape === 'function') return RegExp.escape(string);

  // Fallback implementation for older browsers
  return string.replace(/[.*+?^${}()|[\\\]]/g, '\\$&');
};

/**
 * Checks if the given HTML does not contain anything but whitespace.
 * Mirrors the Ruby implementation of contains_only_whitespace? in has_richdown.rb
 * @param {string} input - The HTML string to check
 * @returns {boolean} True if the text contains only whitespace, false otherwise
 */
export const containsOnlyWhitespace = (input) => {
  // blank input is considered all whitespace
  if (input == null || String(input).trim() === '') return true;
  // return false unless it looks like html
  if (!String(input).includes('<')) return false;

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(input), 'text/html');
  const fragment = doc.body;

  // any non-p element means not whitespace-only
  if (fragment.querySelector('*:not(p)')) return false;

  // ensure no non-whitespace text inside any p
  for (const p of fragment.querySelectorAll('p')) {
    if ((p.textContent || '').trim() !== '') return false;
  }

  return true;
};

/**
 * Checks if the given HTML text contains only entity tags within p tags
 * Mirrors the Ruby implementation of contains_only_entities? in has_richdown.rb
 * @param {string} input - The HTML string to check
 * @returns {boolean} True if the text contains only entities, false otherwise
 */
export const containsOnlyEntities = (input) => {
  // Return false if input is blank
  if (!input || input.trim() === '') return false;

  // Parse the HTML fragment similar to Nokogiri::HTML.fragment
  const parser = new DOMParser();
  const doc = parser.parseFromString(input, 'text/html');
  const fragment = doc.body;

  // Check if there are NO p tags (not "valid" but it incorrectly triggers only-entity format)
  const pTags = fragment.querySelectorAll('p');
  if (pTags.length === 0) return false;

  // Check if there are any non-p elements other than entity tags at the root level
  for (const child of fragment.children) {
    const tagName = child.tagName.toLowerCase();
    if (tagName !== 'p') {
      return false;
    }
  }

  // Check each p tag
  let hasEntity = false;
  for (const p of pTags) {
    // Check all descendants of p tag to ensure no nested non-entity elements
    const allDescendants = p.querySelectorAll('*');
    for (const element of allDescendants) {
      const tagName = element.tagName.toLowerCase();
      if (tagName !== 'entity') {
        // Found a non-entity element inside p tag
        return false;
      }
      if (tagName === 'entity') {
        hasEntity = true;
      }
    }

    // Check direct children for text content
    for (const child of p.childNodes) {
      // If it's a text node and has non-whitespace content, return false
      if (child.nodeType === Node.TEXT_NODE && child.textContent.trim() !== '') {
        return false;
      }
    }
  }

  // must contain at least one entity
  return hasEntity;
};

/**
 * Returns all entity tags from the given HTML string
 * @param {string} input - The HTML string to extract entities from
 * @returns {Array} An array of entity tags
 */
export const extractEntities = (input) => {
  if (!(input || '').includes('<entity')) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(input, 'text/html');
  const fragment = doc.body;

  const entities = [];
  const elements = fragment.querySelectorAll('entity');

  for (const element of elements) {
    const entity = { type: element.dataset.type };
    if (element.dataset.target) entity.target = element.dataset.target;
    if (element.dataset.id) entity.id = element.dataset.id;
    if (element.dataset.name) entity.name = element.dataset.name;
    entities.push(entity);
  }

  return entities;
};

/**
 * Checks if the given HTML text contains only emoji entity tags
 * @param {string} input - The HTML string to check
 * @returns {boolean} True if the text contains only emoji entities, false otherwise
 */
export const containsOnlyEmojiEntities = (input) => {
  if (!containsOnlyEntities(input)) return false;
  const entities = extractEntities(input);
  return entities.length > 0 && entities.every((entity) => entity.type === 'emoji');
};

/**
 * Returns the appropriate separator for Oxford comma formatting in a list
 * @param {number} index - The current index in the list (0-based)
 * @param {number} totalLength - The total length of the list
 * @returns {string} The separator string: '' for last item, ' and ' for second-to-last in 2-item list, ', and ' for second-to-last in 3+ item list, ', ' otherwise
 */
export const oxfordComma = (index, totalLength) => {
  if (index === totalLength - 1) return '';
  if (index === totalLength - 2) return totalLength > 2 ? ', and ' : ' and ';
  return ', ';
};

/**
 * Format a Unix timestamp (seconds) as a short date string
 * @param {number} timestampSeconds - Unix timestamp in seconds (from Rails backend)
 * @returns {string|null} Formatted date like "1/30/2024" or null if invalid
 */
export const formatShortDate = (timestampSeconds) => {
  if (!timestampSeconds) return null;
  return new Date(timestampSeconds * 1000).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
};
